// Onglet Discussions d'un dossier, en trois colonnes : le salon du dossier,
// le fil, puis le contexte (membres, messages épinglés, fichiers cités).
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChannelView } from '../messaging/ChannelView';
import { Spinner } from '../ui/Spinner';
import { Avatar } from '../ui/Avatar';
import { FaHashtag, FaThumbtack } from '../ui/FaIcons';
import {
  useDossierChannel,
  useDossierChannelMembers,
  useMessageAttachments,
  useMessages,
  usePinnedMessages,
  useSetMessagePinned,
} from '../../hooks/queries/useMessaging';
import { useAuthStore } from '../../store/useAuthStore';
import { canAccessFeature, asCollaboratorRole } from '../../services/auth/permissions';
import { formatAppDate } from '../../lib/dateFormat';
import { DossierChannelMembersModal } from './DossierChannelMembersModal';

interface Props {
  dossierId: string;
}

const ROLE_SHORT: Record<string, string> = {
  admin: 'Admin',
  lawyer: 'Avocat',
  secretary: 'Assistant(e)',
};

function jumpToMessage(messageId: string) {
  const el = document.getElementById(messageId);
  if (!el) {
    toast.message('Ce message est plus haut dans le fil : remontez pour le charger.');
    return;
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('bg-cime-gold/20');
  setTimeout(() => el.classList.remove('bg-cime-gold/20'), 2000);
}

export function DossierDiscussionTab({ dossierId }: Props) {
  const { data: channel, isLoading } = useDossierChannel(dossierId);
  const collaborator = useAuthStore((s) => s.collaborator);
  const canInvite = canAccessFeature(
    asCollaboratorRole(collaborator?.role),
    'dossier_channel_invite',
  );
  const [showMembers, setShowMembers] = useState(false);

  const { data: members = [] } = useDossierChannelMembers(channel?.id);
  const { data: pinned = [] } = usePinnedMessages(channel?.id);
  const setPinned = useSetMessagePinned();
  const { data: messages = [] } = useMessages(channel?.id ?? null);
  const messageIds = useMemo(() => messages.map((m) => m.id), [messages]);
  const { data: attachmentsByMessage } = useMessageAttachments(channel?.id ?? null, messageIds);
  const files = useMemo(
    () =>
      [...(attachmentsByMessage?.values() ?? [])]
        .flat()
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 6),
    [attachmentsByMessage],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-neutral-200 bg-white py-20 text-cime-text-muted">
        <p className="text-sm">Aucun salon de discussion n'est associé à ce dossier.</p>
        <p className="text-xs mt-2">
          Le salon est créé automatiquement à la création du dossier. Contactez un administrateur si
          ce dossier en est dépourvu.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-290px)] min-h-[460px] overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <aside className="hidden w-[230px] flex-shrink-0 flex-col border-r border-[#e6e2d8] bg-[#fbfaf7] px-2 py-3 lg:flex">
        <RailTitle>Équipe du cabinet</RailTitle>
        <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 text-[13px] font-semibold text-cime-primary shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
          <FaHashtag className="h-3 w-3 flex-shrink-0 opacity-70" aria-hidden />
          <span className="truncate">Général</span>
        </div>
        <p className="mt-3 px-1 text-xs leading-snug text-neutral-400">
          Un salon par dossier. Il réunit l'équipe du dossier et les personnes invitées.
        </p>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <ChannelView
          channel={channel}
          pinnable
          compactHeader={{
            title: 'Général',
            note: `équipe du dossier · ${members.length} membre${members.length > 1 ? 's' : ''}`,
          }}
        />
      </div>

      <aside className="hidden w-[260px] flex-shrink-0 flex-col overflow-y-auto border-l border-[#e6e2d8] bg-[#fbfaf7] xl:flex">
        <RailSection title="Membres" count={members.length}>
          <div className="flex flex-col gap-2">
            {members.map((member) => (
              <div key={member.collaboratorId} className="flex items-center gap-2.5 text-[13px]">
                <Avatar name={member.fullName} src={member.avatarUrl} size="xs" className="h-6 w-6 text-[10px]" />
                <span className="min-w-0 flex-1 truncate text-cime-text-primary">{member.fullName}</span>
                <span className="flex-shrink-0 text-[11px] text-neutral-400">
                  {member.source === 'manual' ? 'Invité' : (ROLE_SHORT[member.role] ?? member.role)}
                </span>
              </div>
            ))}
          </div>
          {canInvite ? (
            <button
              type="button"
              onClick={() => setShowMembers(true)}
              className="mt-2.5 text-xs font-semibold text-cime-primary hover:underline"
            >
              Gérer les invités
            </button>
          ) : null}
        </RailSection>

        <RailSection title="Épinglés" count={pinned.length}>
          {pinned.length === 0 ? (
            <p className="text-xs leading-snug text-neutral-400">
              Aucun message épinglé. Clic droit sur un message, puis la punaise.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {pinned.map((message) => (
                <div key={message.id} className="group rounded-md border border-[#e6e2d8] bg-white px-2.5 py-2">
                  <button
                    type="button"
                    onClick={() => jumpToMessage(message.id)}
                    className="block w-full text-left text-[13px] leading-snug text-cime-text-primary hover:text-cime-primary"
                  >
                    <span className="line-clamp-3">{message.body}</span>
                  </button>
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-neutral-400">
                    <span className="truncate">
                      {message.author?.full_name ?? '—'} · {formatAppDate(message.createdAt, { year: false })}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPinned.mutate(
                          { id: message.id, channelId: channel.id, pinned: false },
                          { onError: (e) => toast.error(e instanceof Error ? e.message : 'Impossible de désépingler') },
                        )
                      }
                      title="Désépingler"
                      aria-label="Désépingler"
                      className="flex-shrink-0 rounded p-0.5 text-cime-gold-text opacity-60 hover:opacity-100"
                    >
                      <FaThumbtack className="h-3 w-3" aria-hidden />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </RailSection>

        <RailSection title="Fichiers cités" count={files.length}>
          {files.length === 0 ? (
            <p className="text-xs text-neutral-400">Aucun fichier joint dans les messages chargés.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {files.map((file) => (
                <a
                  key={file.id}
                  href={file.signed_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2.5 rounded-md px-1 py-1 hover:bg-white"
                >
                  <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-md bg-[#f1efe8] text-[10px] font-bold text-cime-text-muted">
                    {(file.filename.split('.').pop() ?? '').slice(0, 4).toUpperCase()}
                  </span>
                  <span className="min-w-0 leading-tight">
                    <span className="block truncate text-[13px] text-cime-text-primary">{file.filename}</span>
                    <span className="block text-[11px] text-neutral-400">
                      {formatAppDate(file.created_at, { year: false })}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          )}
        </RailSection>
      </aside>

      {showMembers && (
        <DossierChannelMembersModal
          channelId={channel.id}
          onClose={() => setShowMembers(false)}
        />
      )}
    </div>
  );
}

function RailTitle({ children }: { children: string }) {
  return (
    <h4 className="mb-1.5 px-1 text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">
      {children}
    </h4>
  );
}

function RailSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-[#e6e2d8] px-4 py-3.5 last:border-b-0">
      <h4 className="mb-2.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-400">
        {title}
        <span className="font-semibold tabular-nums">{count}</span>
      </h4>
      {children}
    </section>
  );
}
